import { useEffect, useMemo, useState } from "react";
import { PlayCircle } from "lucide-react";
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { resolveImageUrl } from "@/lib/api";

export const CinematicVideoDialogContent = ({
  open,
  videoUrl,
  posterUrl,
  title,
  description,
  noVideoLabel,
  clickToPlayLabel,
  testIdPrefix,
}) => {
  const [playerReady, setPlayerReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setPlayerReady(false);
    }
  }, [open]);

  const resolvedPoster = useMemo(() => resolveImageUrl(posterUrl), [posterUrl]);
  const resolvedVideo = useMemo(() => {
    if (!open || !playerReady || !videoUrl) {
      return null;
    }
    return resolveImageUrl(videoUrl) || null;
  }, [open, playerReady, videoUrl]);

  return (
    <DialogContent
      className="max-w-5xl border border-[#D4AF37]/35 bg-[#0A0A0D]/95 p-0 text-cream-50 shadow-[0_30px_90px_rgba(0,0,0,0.75)] backdrop-blur-xl"
      data-testid={`${testIdPrefix}-dialog-content`}
    >
      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-4 sm:p-5" data-testid={`${testIdPrefix}-glass-wrapper`}>
        <DialogHeader className="mb-3 px-1 text-start">
          <DialogTitle className="font-['Playfair_Display'] text-xl text-cream-50" data-testid={`${testIdPrefix}-dialog-title`}>
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-cream-200/85" data-testid={`${testIdPrefix}-dialog-description`}>
            {description}
          </DialogDescription>
        </DialogHeader>

        {!videoUrl ? (
          <p className="rounded-xl border border-white/10 bg-black/35 px-4 py-6 text-sm text-cream-200" data-testid={`${testIdPrefix}-no-video`}>
            {noVideoLabel}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[#D4AF37]/30 bg-black" data-testid={`${testIdPrefix}-stage`}>
            {!playerReady && (
              <button
                type="button"
                className="group relative block w-full"
                onClick={() => setPlayerReady(true)}
                data-testid={`${testIdPrefix}-prepare-play-button`}
              >
                <div className="aspect-video w-full bg-stone-900">
                  {resolvedPoster ? (
                    <img
                      src={resolvedPoster}
                      alt={title}
                      className="h-full w-full object-cover opacity-80 transition duration-300 group-hover:opacity-95"
                      loading="lazy"
                      data-testid={`${testIdPrefix}-poster-image`}
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-stone-800 to-black" data-testid={`${testIdPrefix}-poster-fallback`} />
                  )}
                </div>
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 bg-black/70 px-4 py-2 text-sm text-cream-50">
                    <PlayCircle size={16} /> {clickToPlayLabel}
                  </span>
                </span>
              </button>
            )}

            {playerReady && resolvedVideo && (
              <video
                controls
                playsInline
                preload="metadata"
                poster={resolvedPoster || undefined}
                className="aspect-video w-full bg-black object-contain"
                src={resolvedVideo}
                data-testid={`${testIdPrefix}-video-player`}
              />
            )}
          </div>
        )}
      </div>
    </DialogContent>
  );
};
